import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useActualIncidentId } from './commonHooks';
import { useSelector } from 'react-redux';
import { useRecordIcs201Version } from 'components/IAP/planningView/widgets/ics201VersionRecordingContext';
import { selectUserGuid } from '../../../slices/commonSelectors';
import config from '../../../constants/apiConfig';
import {
  keys as actionKeys,
  CreateActionReqData,
  useCreateAction,
} from './actionHooks';
import { toast } from 'react-toastify';
import { ActionBase } from '../../../types/iapBaseTypes';
import { toUtcIso } from '../../../utils/dateTime';
import {
  ActionAssigneeKind,
  DEFAULT_ACTION_PRIORITY,
  IapScope,
  INCIDENT,
} from '../ICSConstants';
import { DEFAULT_ACTION_CLASSIFICATION } from '../actionClassification';
import type { GeoLocation } from 'components/GeoLocationComponent/buildGeoLocationFromGeoJson';
import { incidentRosterKeys } from './incidentRoster/incidentRoster.queryKeys';
import type { IncidentRoster } from './incidentRoster/incidentRoster.types';
import type { ActionDependencyBlocker } from '../utils/actionDependencies';
import { actionErrorMessage } from './actionErrors';
import type { ActionTicketFieldValues } from '../actionTicket/actionTicketModel';

export type IncidentActionBase = ActionBase &
  ActionTicketFieldValues & {
    id: UUID;
    action_id: UUID;
    display_id?: string | null;
    aor_id?: string | null;
    aor_name?: string | null;
    workspace_name?: string | null;
    poc: string | null;
    briefed_poc: boolean;
    start_date: Date | null;
    status: string | null;
    target_date: Date | null;
    actual_date: Date | null;
    resources?: UUID[];
    geolocation?: GeoLocation | null;
    utc_offset?: number;
    parent_id?: UUID | null;
    priority?: string | null;
    assignee_kind?: ActionAssigneeKind | null;
    assignee_id?: string | null;
    /** Occupants named as responsible when assigned to an ICS position. */
    responsible_individual_ids?: string[];
    /**
     * Predecessor `action_id`s. Omit to leave existing dependencies alone; send
     * an empty array to clear them.
     */
    depends_on?: UUID[];
  };

export type EditIncidentActionRequest = IncidentActionBase;

export type GetIncidentActionResponse = IncidentActionBase & {
  group_guid: UUID;
  incident_id: UUID;
  user_guid: UUID;
  resource_details?: any[];
  user_name?: string;
  poc_user_name?: string;
  version: number;
  timestamp: Date;
  index?: number;
  /** The predecessors still open. Non-empty means this task is blocked. */
  blocked_by?: ActionDependencyBlocker[];
};

export const keys = {
  base: ['incident_actions'] as const,
  byIncidentAndUser: (incidentId: string, userId: string) =>
    [...keys.base, incidentId, userId] as const,
  versions: (incidentId: string, actionId: string) =>
    [...keys.base, 'versions', incidentId, actionId] as const,
} as const;

export const UNTITLED_TASK_NAME = 'Untitled task';

export const useIncidentActionVersions = (
  incidentId: string | null | undefined,
  actionId: string | null | undefined,
  enabled = true
) => {
  return useQuery({
    queryKey: keys.versions(incidentId ?? '', actionId ?? ''),
    queryFn: async () => {
      const resp = await axios.get<GetIncidentActionResponse[]>(
        `${config.apiGateway.ics_incident_actions}/${incidentId}/${actionId}/versions`
      );
      return resp.data.map((row) => ({
        ...row,
        timestamp: toUtcIso(
          row.timestamp
        ) as GetIncidentActionResponse['timestamp'],
      }));
    },
    enabled: enabled && !!incidentId && !!actionId,
  });
};

// Fetch all actions. Optional incidentIdOverride is for Report/sitrep contexts
// where the sitrep's incident may differ from the route/Redux incident.
export const useIncidentActions = (
  scope: IapScope = INCIDENT,
  incidentIdOverride?: string | null
) => {
  const routeIncidentId = useActualIncidentId();
  const incidentId = incidentIdOverride || routeIncidentId;
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.byIncidentAndUser(incidentId, user_guid),
    queryFn: async () => {
      const resp = await axios.get<GetIncidentActionResponse[]>(
        `${config.apiGateway.ics_incident_actions}/${incidentId}`
      );
      return resp.data.map((a) => ({
        ...a,
        start_date: toUtcIso(
          a.start_date
        ) as GetIncidentActionResponse['start_date'],
        target_date: toUtcIso(
          a.target_date
        ) as GetIncidentActionResponse['target_date'],
        actual_date: toUtcIso(
          a.actual_date
        ) as GetIncidentActionResponse['actual_date'],
        timestamp: toUtcIso(
          a.timestamp
        ) as GetIncidentActionResponse['timestamp'],
      }));
    },
    enabled: scope === INCIDENT && !!incidentId,
  });
};

export const useCreateIncidentAction = (incidentIdOverride?: string | null) => {
  const routeIncidentId = useActualIncidentId();
  const incidentId = incidentIdOverride || routeIncidentId;
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useCreateAction();
  const recordIcs201Version = useRecordIcs201Version();
  return {
    ...mutation,
    mutateAsync: (data: CreateActionReqData) => {
      data.incident_id = incidentId;
      return mutation.mutateAsync(data, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: actionKeys.all });
          queryClient.invalidateQueries({
            queryKey: keys.byIncidentAndUser(incidentId, user_guid),
          });
          recordIcs201Version();
        },
      });
    },
  };
};

type InstantiateIncidentTaskInput = {
  status?: string | null;
  assigneeKind?: ActionAssigneeKind;
  assigneeId?: string | null;
  aorId?: string | null;
};

/**
 * Incident create returns a version row (action_id + tip metadata). Org create
 * typing on axios is narrower — coerce so the drawer banner always has fields.
 */
export function normalizeInstantiatedIncidentTask(
  raw: unknown
): GetIncidentActionResponse {
  const row = (raw ?? {}) as Partial<GetIncidentActionResponse> &
    Record<string, unknown>;
  const versionId = String(row.id ?? '');
  const actionId = String(row.action_id ?? versionId);
  const timestamp =
    (toUtcIso(row.timestamp as Date | string | null | undefined) as
      | GetIncidentActionResponse['timestamp']
      | undefined) || (new Date().toISOString() as unknown as Date);

  return {
    ...(row as GetIncidentActionResponse),
    id: (versionId || actionId) as UUID,
    action_id: actionId as UUID,
    version: typeof row.version === 'number' ? row.version : 1,
    timestamp,
    user_name: typeof row.user_name === 'string' ? row.user_name : undefined,
    status: (row.status as string | null | undefined) ?? 'Planned',
    name: typeof row.name === 'string' ? row.name : UNTITLED_TASK_NAME,
  };
}

/** Instantiate then hand the normalized row to the open-edit drawer callback. */
export async function openInstantiatedIncidentTask(args: {
  instantiate: (
    input: InstantiateIncidentTaskInput
  ) => Promise<GetIncidentActionResponse>;
  onOpen: (row: GetIncidentActionResponse) => void;
} & InstantiateIncidentTaskInput): Promise<GetIncidentActionResponse | null> {
  const { instantiate, onOpen, ...input } = args;
  try {
    const row = normalizeInstantiatedIncidentTask(await instantiate(input));
    if (!row.action_id) {
      toast.error('Task was created but is missing an id. Open it from the list.');
      return null;
    }
    onOpen(row);
    return row;
  } catch {
    return null;
  }
}

/** Writes version 1 immediately so the drawer opens on a real task row. */
export const useInstantiateIncidentTask = (
  incidentIdOverride?: string | null
) => {
  const create = useCreateIncidentAction(incidentIdOverride);
  const instantiate = async (
    input: InstantiateIncidentTaskInput
  ): Promise<GetIncidentActionResponse> => {
    const kind = input.assigneeKind ?? 'user';
    const assigneeId = input.assigneeId?.trim() || null;
    const response = await create.mutateAsync({
      name: UNTITLED_TASK_NAME,
      description: '',
      checklist: [],
      status: input.status || 'Planned',
      priority: DEFAULT_ACTION_PRIORITY,
      assignee_kind: assigneeId ? kind : null,
      assignee_id: assigneeId,
      poc: kind === 'user' ? assigneeId : null,
      aor_id: input.aorId ?? null,
      archived: false,
      classification: DEFAULT_ACTION_CLASSIFICATION,
      briefed_poc: false,
      parent_id: null,
      parentId: null,
    } as CreateActionReqData);
    return normalizeInstantiatedIncidentTask(response.data);
  };
  return { instantiate, isPending: create.isPending };
};

export const useUpdateIncidentAction = (incidentIdOverride?: string | null) => {
  const user_guid = useSelector(selectUserGuid);
  const routeIncidentId = useActualIncidentId();
  const incidentId = incidentIdOverride || routeIncidentId;
  const queryClient = useQueryClient();
  const recordIcs201Version = useRecordIcs201Version();
  return useMutation({
    mutationFn: async (data: EditIncidentActionRequest) => {
      // Ensure archived field is included with a default value
      const updateData = {
        ...data,
        archived: data.archived !== undefined ? data.archived : false,
        user_guid,
      };
      return await axios.put(
        `${config.apiGateway.ics_incident_actions}/${incidentId}/${data.id}`,
        updateData
      );
    },
    onSuccess: (_, variables) => {
      // Form payload has `poc` but usually no display name. Resolve from roster
      // (same source as the Assignee dropdown) so the table updates immediately,
      // then refetch so the server COALESCE fills poc_user_name.
      const roster = queryClient.getQueryData<IncidentRoster>(
        incidentRosterKeys.byIncident(incidentId)
      );
      const member = roster?.find((m) => m.userId === variables.poc);
      const resolvedPocName = variables.poc
        ? member?.userName?.trim() ||
          member?.profileSettings?.userName?.trim() ||
          member?.emailAddress ||
          null
        : null;

      queryClient.setQueryData<GetIncidentActionResponse[]>(
        keys.byIncidentAndUser(incidentId, user_guid),
        (old) =>
          old?.map((a) =>
            a.id === variables.id
              ? {
                  ...a,
                  ...variables,
                  poc: variables.poc,
                  poc_user_name: variables.poc
                    ? (resolvedPocName ?? a.poc_user_name)
                    : undefined,
                }
              : a
          )
      );

      void queryClient.invalidateQueries({
        queryKey: keys.byIncidentAndUser(incidentId, user_guid),
      });
      queryClient.invalidateQueries({ queryKey: actionKeys.all });
      recordIcs201Version();
    },
    onError: (error) => {
      toast.error(actionErrorMessage(error));
    },
  });
};
